import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';

const defaultOptions = {
  sessionId: null,
  query: null,
  limit: 24,
  type: 'products',
};

const usePagination = (opts) => {
  const { sessionId, query, type, limit } = {
    ...defaultOptions,
    ...opts,
  };

  if (!sessionId || !query || !type) {
    throw new Error('The parameters: sessionId, query and type are mandatory');
  }

  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const { data, error, fetchMore, loading, refetch } = useQuery(query, {
    variables: { sessionId, limit },
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      // Check if we've loaded all items
      if (result && result[type] && !result[type].nextToken) {
        setAllLoaded(true);
      }
    },
  });

  const items = data ? data[type] : { nextToken: null };
  const hasNextToken = Boolean(items.nextToken);

  // Function to load more items when button is clicked
  const loadMore = useCallback(async () => {
    if (!hasNextToken || loading) {
      return;
    }

    setLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          nextToken: items.nextToken,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prevResult;
          }

          const newItems = {
            [type]: {
              items: [
                ...prevResult[type].items,
                ...fetchMoreResult[type].items,
              ],
              nextToken: fetchMoreResult[type].nextToken,
              // eslint-disable-next-line no-underscore-dangle
              __typename: prevResult[type].__typename,
            },
          };

          // Check if this was the last page
          if (!fetchMoreResult[type].nextToken) {
            setAllLoaded(true);
          }

          setLoadingMore(false);
          return newItems;
        },
      });
    } catch (err) {
      /* eslint-disable no-console */
      console.error('Error loading more items:', err);
      setLoadingMore(false);
    }
  }, [hasNextToken, items.nextToken, fetchMore, type, loadingMore]);

  return {
    data,
    loading,
    error,
    refetch,
    allLoaded,
    loadMore,
    loadingMore,
    hasNextToken,
  };
};

export default usePagination;
